import React, { useState, useEffect, useCallback } from 'react';
import { Truck, TrendingUp, DollarSign, AlertTriangle, Download, Filter, Calendar, ChevronDown, CheckCircle, ArrowLeft, Package, Users, MapPin, FolderOpen, Building2, ArrowDownToLine } from 'lucide-react';
import { dispatchAPI, branchAPI, fetchPaged, inwardAPI } from '../../services/api';
import { toast } from 'sonner';
import Pagination from '../ui/Pagination';

const fmt = (v) => {
  if (!v && v !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(v);
};

const PRESETS = [
  { label: 'This Month', getValue: () => { const n = new Date(); return { start: `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-01`, end: '' }; }},
  { label: 'Last 3 Months', getValue: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth()-2, 1); return { start: s.toISOString().slice(0,10), end: '' }; }},
  { label: 'This FY', getValue: () => { const n = new Date(); const yr = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear()-1; return { start: `${yr}-04-01`, end: '' }; }},
  { label: 'All Time', getValue: () => ({ start: '', end: '' }) },
];

const TAB_LIST = [
  { key: 'product', label: 'Product-wise', icon: Package },
  { key: 'customer', label: 'Customer-wise', icon: Users },
  { key: 'branch', label: 'Branch-wise', icon: Building2 },
  { key: 'project', label: 'Project-wise', icon: FolderOpen },
  { key: 'transporter', label: 'Transporter-wise', icon: Truck },
  { key: 'inward', label: 'Inward Freight', icon: ArrowDownToLine },
];

const FreightTracker = ({ onBack }) => {
  const [analytics, setAnalytics] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('product');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedPreset, setSelectedPreset] = useState('All Time');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState('');
  const [search, setSearch] = useState('');
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount_paid: 0, payment_date: '', payment_mode: '', notes: '' });
  const [inwardAnalytics, setInwardAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateRange.start) params.start_date = dateRange.start;
      if (dateRange.end) params.end_date = dateRange.end;

      const [analyticsData, dispatchResult, inwardData] = await Promise.all([
        dispatchAPI.getFreightAnalytics(params),
        fetchPaged('/dispatches/freight/dispatches', {
          ...params, page, page_size: 20,
          ...(paymentFilter ? { payment_status: paymentFilter } : {}),
          ...(search ? { search } : {}),
        }),
        inwardAPI.getFreightAnalytics(params).catch(() => null),
      ]);
      setAnalytics(analyticsData);
      setDispatches(dispatchResult.items);
      setTotalPages(dispatchResult.total_pages);
      setTotalItems(dispatchResult.total);
      setInwardAnalytics(inwardData);
    } catch (err) { console.error(err); toast.error('Failed to load freight data'); }
    finally { setLoading(false); }
  }, [dateRange, page, paymentFilter, search]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handlePreset = (preset) => {
    const val = preset.getValue();
    setDateRange(val);
    setSelectedPreset(preset.label);
    setPage(1);
  };

  const handleMarkPaid = async () => {
    if (!payModal || !payForm.amount_paid) return;
    try {
      await dispatchAPI.markFreightPaid(payModal.id, payForm);
      toast.success('Freight payment recorded');
      setPayModal(null);
      setPayForm({ amount_paid: 0, payment_date: '', payment_mode: '', notes: '' });
      fetchAnalytics();
    } catch (err) { toast.error('Failed to record payment'); }
  };

  const exportCSV = (data, columns, filename) => {
    const header = columns.map(c => c.label).join(',');
    const rows = data.map(row => columns.map(c => `"${row[c.key] ?? ''}"`).join(','));
    const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}.csv`;
    a.click();
  };

  const s = analytics?.summary || {};

  return (
    <div className="p-5 lg:p-6" data-testid="freight-tracker">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="freight-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Freight Tracker</h1>
            <p className="text-xs text-gray-500 mt-0.5">Analytics, costs & payment tracking</p>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-5 bg-white border border-gray-100 rounded-xl p-3" data-testid="freight-date-filter">
        <Calendar className="w-4 h-4 text-gray-400" />
        <div className="flex gap-1">
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => handlePreset(p)} data-testid={`preset-${p.label.toLowerCase().replace(/ /g,'-')}`}
              className={`px-3 py-1.5 text-[11px] font-semibold rounded-lg transition ${selectedPreset === p.label ? 'bg-[#24549c] text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <input type="date" value={dateRange.start} onChange={e => { setDateRange(d => ({...d, start: e.target.value})); setSelectedPreset(''); setPage(1); }}
            className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" data-testid="freight-start-date" />
          <span className="text-gray-400 text-xs">to</span>
          <input type="date" value={dateRange.end} onChange={e => { setDateRange(d => ({...d, end: e.target.value})); setSelectedPreset(''); setPage(1); }}
            className="px-2 py-1.5 text-[11px] border border-gray-200 rounded-lg" data-testid="freight-end-date" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-gray-400"><Truck className="w-5 h-5 animate-pulse mr-2" /> Loading freight data...</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="freight-summary-cards">
            {[
              { label: 'Total Freight', value: s.total_freight, color: '#24549c', icon: DollarSign },
              { label: 'Advance Paid', value: s.advance_paid, color: '#059669', icon: CheckCircle },
              { label: 'Rest Pending', value: s.rest_pending, color: '#dc2626', icon: AlertTriangle },
              { label: 'Pending Dispatches', value: s.dispatches_with_pending, color: '#f59e0b', icon: Truck, isCount: true },
              { label: 'Avg per Dispatch', value: s.avg_freight, color: '#6366f1', icon: TrendingUp },
              { label: 'Total Dispatches', value: s.total_dispatches, color: '#8b5cf6', icon: Package, isCount: true },
            ].map((card, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl p-3.5" data-testid={`summary-card-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <card.icon className="w-4 h-4" style={{ color: card.color }} />
                  <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{card.label}</span>
                </div>
                <p className="text-lg font-bold" style={{ color: card.color }}>
                  {card.isCount ? card.value : `₹${fmt(card.value)}`}
                </p>
              </div>
            ))}
          </div>

          {/* Monthly Trend */}
          {analytics?.monthly?.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl p-4 mb-6" data-testid="monthly-trend">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-900">Monthly Freight Trend</h3>
                <button onClick={() => exportCSV(analytics.monthly, [
                  {key:'month',label:'Month'},{key:'total_freight',label:'Total Freight'},{key:'advance',label:'Advance'},{key:'rest',label:'Rest'},{key:'freight_pct',label:'Freight %'},{key:'count',label:'Dispatches'}
                ], 'monthly_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 hover:text-gray-700 bg-gray-50 rounded-lg">
                  <Download className="w-3 h-3" /> CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1 min-w-[400px]" style={{ height: '160px' }}>
                  {analytics.monthly.map((m, i) => {
                    const maxF = Math.max(...analytics.monthly.map(x => x.total_freight));
                    const h = maxF ? (m.total_freight / maxF) * 140 : 0;
                    return (
                      <div key={i} className="flex flex-col items-center flex-1 min-w-[40px]" title={`${m.month}: ₹${fmt(m.total_freight)} (${m.freight_pct}%)`}>
                        <span className="text-[9px] text-gray-500 mb-1">₹{fmt(m.total_freight)}</span>
                        <div className="w-full max-w-[32px] rounded-t-md" style={{ height: `${Math.max(h, 4)}px`, backgroundColor: '#24549c', opacity: 0.8 }} />
                        <span className="text-[8px] text-gray-400 mt-1 whitespace-nowrap">{m.month}</span>
                        <span className="text-[8px] text-blue-500 font-semibold">{m.freight_pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Analytics Tabs */}
          <div className="bg-white border border-gray-100 rounded-xl mb-6" data-testid="analytics-tabs">
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {TAB_LIST.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-4 py-3 text-[11px] font-semibold whitespace-nowrap border-b-2 transition ${tab === t.key ? 'border-[#24549c] text-[#24549c]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  <t.icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {tab === 'product' && <ProductTab data={analytics?.product_wise || []} totalFreight={s.total_freight} exportCSV={exportCSV} />}
              {tab === 'customer' && <CustomerTab data={analytics?.customer_wise || []} exportCSV={exportCSV} />}
              {tab === 'branch' && <BranchTab data={analytics?.branch_wise || []} exportCSV={exportCSV} />}
              {tab === 'project' && <ProjectTab data={analytics?.project_wise || []} exportCSV={exportCSV} />}
              {tab === 'transporter' && <TransporterTab data={analytics?.transporter_wise || []} exportCSV={exportCSV} />}
              {tab === 'inward' && <InwardTab data={inwardAnalytics} exportCSV={exportCSV} />}
            </div>
          </div>

          {/* Dispatch Freight Table */}
          <div className="bg-white border border-gray-100 rounded-xl" data-testid="freight-dispatch-table">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">All Dispatches — Freight Details</h3>
              <div className="flex items-center gap-2">
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search DSP#, OC#, customer..."
                  className="px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-lg w-48" data-testid="freight-search" />
                <select value={paymentFilter} onChange={e => { setPaymentFilter(e.target.value); setPage(1); }}
                  className="px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-lg" data-testid="freight-payment-filter">
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-gray-50/60">
                  {['DSP #','OC #','Customer','Transporter','Vehicle','Total Freight','Advance','Rest Freight','Status','Action'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {dispatches.map(d => (
                    <tr key={d.id} className="border-t border-gray-50 hover:bg-gray-50/40">
                      <td className="px-3 py-2.5 text-xs font-mono font-semibold text-[#24549c]">{d.dispatch_number}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{d.oc_number || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-700">{d.customer_name || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{d.transporter_name || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{d.vehicle_no || '-'}</td>
                      <td className="px-3 py-2.5 text-xs font-semibold text-gray-900">₹{fmt(d.total_freight)}</td>
                      <td className="px-3 py-2.5 text-xs text-emerald-600">₹{fmt(d.advance_freight)}</td>
                      <td className={`px-3 py-2.5 text-xs font-semibold ${d.rest_freight > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        ₹{fmt(d.rest_freight)}
                      </td>
                      <td className="px-3 py-2.5">
                        {d.rest_freight > 0
                          ? <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 rounded-full">Pending</span>
                          : <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 rounded-full">Paid</span>
                        }
                      </td>
                      <td className="px-3 py-2.5">
                        {d.rest_freight > 0 && (
                          <button onClick={() => { setPayModal(d); setPayForm({ amount_paid: d.rest_freight, payment_date: new Date().toISOString().slice(0,10), payment_mode: '', notes: '' }); }}
                            className="px-2.5 py-1 text-[10px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid={`mark-paid-${d.id}`}>
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {dispatches.length === 0 && (
                    <tr><td colSpan={10} className="px-3 py-8 text-center text-xs text-gray-400">No dispatches found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="p-3 border-t border-gray-100">
                <Pagination page={page} totalPages={totalPages} totalItems={totalItems} pageSize={20} onPageChange={setPage} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Mark Paid Modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="mark-paid-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Record Freight Payment — {payModal.dispatch_number}</h3>
            <div className="text-xs text-gray-500 mb-3">Total Freight: ₹{fmt(payModal.total_freight)} | Advance: ₹{fmt(payModal.advance_freight)} | Rest: ₹{fmt(payModal.rest_freight)}</div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Amount Paid</label>
                <input type="number" value={payForm.amount_paid} onChange={e => setPayForm(f => ({...f, amount_paid: parseFloat(e.target.value) || 0}))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="pay-amount-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Payment Date</label>
                <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({...f, payment_date: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="pay-date-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Payment Mode</label>
                <select value={payForm.payment_mode} onChange={e => setPayForm(f => ({...f, payment_mode: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="pay-mode-select">
                  <option value="">Select</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Notes</label>
                <input value={payForm.notes} onChange={e => setPayForm(f => ({...f, notes: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="Optional notes" data-testid="pay-notes-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPayModal(null)} className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg" data-testid="pay-cancel-btn">Cancel</button>
              <button onClick={handleMarkPaid} className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg" data-testid="pay-confirm-btn">Record Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// === ANALYTICS TAB COMPONENTS ===

const ProductTab = ({ data, totalFreight, exportCSV }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-500">{data.length} products</p>
      <button onClick={() => exportCSV(data, [
        {key:'product',label:'Product'},{key:'unit',label:'Unit'},{key:'total_qty',label:'Total Qty'},{key:'total_freight',label:'Total Freight'},{key:'freight_per_unit',label:'Freight/Unit'},{key:'pct_of_total',label:'% of Total'}
      ], 'product_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
        <Download className="w-3 h-3" /> Export
      </button>
    </div>
    <table className="w-full" data-testid="product-freight-table">
      <thead><tr className="bg-gray-50/60">
        {['Product','Unit','Total Qty','Total Freight','Freight/Unit','% of Total'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
            <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.product}</td>
            <td className="px-3 py-2 text-xs text-gray-500">{r.unit}</td>
            <td className="px-3 py-2 text-xs text-gray-700">{fmt(r.total_qty)}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_freight)}</td>
            <td className="px-3 py-2 text-xs font-semibold text-blue-700">₹{fmt(r.freight_per_unit)}</td>
            <td className="px-3 py-2"><div className="flex items-center gap-1.5"><div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#24549c] rounded-full" style={{width:`${Math.min(r.pct_of_total,100)}%`}} /></div><span className="text-[10px] text-gray-500">{r.pct_of_total}%</span></div></td>
          </tr>
        ))}
        {data.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-400">No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const CustomerTab = ({ data, exportCSV }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-500">{data.length} customers</p>
      <button onClick={() => exportCSV(data, [
        {key:'customer',label:'Customer'},{key:'dispatches',label:'Dispatches'},{key:'total_order_value',label:'Order Value'},{key:'total_freight',label:'Total Freight'},{key:'freight_pct_of_value',label:'Freight %'},{key:'freight_per_unit',label:'Freight/Unit'}
      ], 'customer_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
        <Download className="w-3 h-3" /> Export
      </button>
    </div>
    <table className="w-full" data-testid="customer-freight-table">
      <thead><tr className="bg-gray-50/60">
        {['Customer','Dispatches','Order Value','Total Freight','Freight % of Value','Freight/Unit'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
            <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.customer}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{r.dispatches}</td>
            <td className="px-3 py-2 text-xs text-gray-700">₹{fmt(r.total_order_value)}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_freight)}</td>
            <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${r.freight_pct_of_value > 10 ? 'bg-red-50 text-red-700' : r.freight_pct_of_value > 5 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{r.freight_pct_of_value}%</span></td>
            <td className="px-3 py-2 text-xs font-semibold text-blue-700">₹{fmt(r.freight_per_unit)}</td>
          </tr>
        ))}
        {data.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-400">No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const BranchTab = ({ data, exportCSV }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-500">{data.length} branches</p>
      <button onClick={() => exportCSV(data, [
        {key:'branch',label:'Branch'},{key:'dispatches',label:'Dispatches'},{key:'total_freight',label:'Total Freight'},{key:'freight_per_unit',label:'Freight/Unit'},{key:'avg_per_dispatch',label:'Avg/Dispatch'}
      ], 'branch_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
        <Download className="w-3 h-3" /> Export
      </button>
    </div>
    <table className="w-full" data-testid="branch-freight-table">
      <thead><tr className="bg-gray-50/60">
        {['Branch','Dispatches','Total Freight','Freight/Unit','Avg per Dispatch'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
            <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.branch}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{r.dispatches}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_freight)}</td>
            <td className="px-3 py-2 text-xs font-semibold text-blue-700">₹{fmt(r.freight_per_unit)}</td>
            <td className="px-3 py-2 text-xs text-gray-700">₹{fmt(r.avg_per_dispatch)}</td>
          </tr>
        ))}
        {data.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-400">No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const ProjectTab = ({ data, exportCSV }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-500">{data.length} projects</p>
      <button onClick={() => exportCSV(data, [
        {key:'project_code',label:'Code'},{key:'project_name',label:'Project'},{key:'dispatches',label:'Dispatches'},{key:'total_project_value',label:'Project Value'},{key:'total_freight',label:'Total Freight'},{key:'freight_pct_of_value',label:'Freight %'},{key:'freight_per_unit',label:'Freight/Unit'}
      ], 'project_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
        <Download className="w-3 h-3" /> Export
      </button>
    </div>
    <table className="w-full" data-testid="project-freight-table">
      <thead><tr className="bg-gray-50/60">
        {['Code','Project','Dispatches','Project Value','Total Freight','Freight %','Freight/Unit'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
            <td className="px-3 py-2 text-xs font-mono font-bold text-blue-700">{r.project_code || '-'}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.project_name}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{r.dispatches}</td>
            <td className="px-3 py-2 text-xs text-gray-700">₹{fmt(r.total_project_value)}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_freight)}</td>
            <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${r.freight_pct_of_value > 10 ? 'bg-red-50 text-red-700' : r.freight_pct_of_value > 5 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{r.freight_pct_of_value}%</span></td>
            <td className="px-3 py-2 text-xs font-semibold text-blue-700">₹{fmt(r.freight_per_unit)}</td>
          </tr>
        ))}
        {data.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-gray-400">No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const TransporterTab = ({ data, exportCSV }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs text-gray-500">{data.length} transporters</p>
      <button onClick={() => exportCSV(data, [
        {key:'transporter',label:'Transporter'},{key:'trips',label:'Trips'},{key:'total_freight',label:'Total Freight'},{key:'advance_paid',label:'Advance Paid'},{key:'rest_pending',label:'Rest Pending'},{key:'avg_per_trip',label:'Avg/Trip'}
      ], 'transporter_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
        <Download className="w-3 h-3" /> Export
      </button>
    </div>
    <table className="w-full" data-testid="transporter-freight-table">
      <thead><tr className="bg-gray-50/60">
        {['Transporter','Trips','Total Freight','Advance Paid','Rest Pending','Avg/Trip'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
      </tr></thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
            <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.transporter}</td>
            <td className="px-3 py-2 text-xs text-gray-600">{r.trips}</td>
            <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_freight)}</td>
            <td className="px-3 py-2 text-xs text-emerald-600">₹{fmt(r.advance_paid)}</td>
            <td className={`px-3 py-2 text-xs font-semibold ${r.rest_pending > 0 ? 'text-red-600' : 'text-emerald-600'}`}>₹{fmt(r.rest_pending)}</td>
            <td className="px-3 py-2 text-xs text-gray-700">₹{fmt(r.avg_per_trip)}</td>
          </tr>
        ))}
        {data.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-gray-400">No data</td></tr>}
      </tbody>
    </table>
  </div>
);

const InwardTab = ({ data, exportCSV }) => {
  if (!data) return <div className="py-6 text-center text-xs text-gray-400">Loading inward freight data...</div>;
  const s = data.summary || {};
  const vendors = data.vendor_wise || [];
  return (
    <div data-testid="inward-freight-tab">
      {/* Inward Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Inward Freight', value: s.total_freight, color: '#f59e0b' },
          { label: 'Loading + Unloading', value: (s.total_loading || 0) + (s.total_unloading || 0), color: '#8b5cf6' },
          { label: 'Total Charges', value: s.total_additional_charges, color: '#dc2626' },
          { label: 'Total Landed Cost', value: s.total_landed_cost, color: '#059669' },
        ].map((c, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-lg p-3">
            <p className="text-[10px] text-gray-500 mb-1">{c.label}</p>
            <p className="text-sm font-bold" style={{ color: c.color }}>₹{fmt(c.value)}</p>
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-500 mb-3">Material Cost: ₹{fmt(s.total_material_cost)} | Total Inwards: {s.inward_count}</div>

      {/* Vendor-wise */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-gray-700">Vendor-wise Inward Freight</p>
        <button onClick={() => exportCSV(vendors, [
          {key:'vendor',label:'Vendor'},{key:'inwards',label:'Inwards'},{key:'total_freight',label:'Freight'},{key:'total_charges',label:'Total Charges'},{key:'freight_per_unit',label:'Charges/Unit'}
        ], 'inward_vendor_freight')} className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 bg-gray-50 rounded-lg hover:text-gray-700">
          <Download className="w-3 h-3" /> Export
        </button>
      </div>
      <table className="w-full" data-testid="inward-vendor-freight-table">
        <thead><tr className="bg-gray-50/60">
          {['Vendor','Inwards','Freight','Total Charges','Charges/Unit'].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase">{h}</th>)}
        </tr></thead>
        <tbody>
          {vendors.map((r, i) => (
            <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/40">
              <td className="px-3 py-2 text-xs font-semibold text-gray-800">{r.vendor}</td>
              <td className="px-3 py-2 text-xs text-gray-600">{r.inwards}</td>
              <td className="px-3 py-2 text-xs font-semibold text-amber-700">₹{fmt(r.total_freight)}</td>
              <td className="px-3 py-2 text-xs font-semibold text-gray-900">₹{fmt(r.total_charges)}</td>
              <td className="px-3 py-2 text-xs font-semibold text-blue-700">₹{fmt(r.freight_per_unit)}</td>
            </tr>
          ))}
          {vendors.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-400">No inward data</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

export default FreightTracker;
